import { useState, useEffect, useCallback, Fragment } from 'react';
import { getWidthScreen } from '/www/assets/src/react_additional_files/functions';
import {
    Button,
    CircularProgress,
    Backdrop,
    Typography,
    ListItemButton,
    ListItemText,
    ListItem,
    Box,
    Divider,
    IconButton,
    Drawer,
    List,
} from '@mui/material';
import 'react-toastify/dist/ReactToastify.css';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

type RubricNode = {
    name: string;
    url: string;
    rubrics?: Record<string, RubricNode>;
    kind?: string;
    parent?: string;
};

type RootRubricMap = Record<string, RubricNode>;
type RubricatorMap = Record<string, RootRubricMap>;

const App = () => {
    const [loader, setLoader] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedRubric, setSelectedRubric] = useState<RubricNode | false>(false);
    const [rubricHistory, setRubricHistory] = useState<Array<RubricNode | false>>([]);
    const [isTransitioning, setIsTransitioning] = useState(false);

    useEffect(() => {
        const handleRubricatorEvent = () => {
            setOpen((prevOpen) => !prevOpen);
        };

        $(document).on('RubricatorHandler', handleRubricatorEvent);
        return () => {
            $(document).off('RubricatorHandler', handleRubricatorEvent);
        };
    }, []);

    useEffect(() => {
        if (open) {
            $('html').css({ overflow: 'hidden', 'padding-right': '16px' });
            return;
        }
        $('html').css({ overflow: 'auto', padding: '0px' });
        setSelectedRubric(false);
        setRubricHistory([]);
    }, [open]);

    const showLoader = useCallback(() => {
        setLoader(true);
    }, []);

    const hideLoader = useCallback(() => {
        setLoader(false);
    }, []);

    const toggleDrawer = useCallback(() => {
        setOpen((prevOpen) => !prevOpen);
    }, []);

    const getDrawerWidth = useCallback(() => {
        const widthScreen = getWidthScreen();
        return widthScreen === 'xl' || widthScreen === 'lg' || widthScreen === 'md' ? '500px' : '100vw';
    }, []);

    const rubricHandle = useCallback(
        (rubric: RubricNode) => () => {
            setIsTransitioning(true);
            setTimeout(() => {
                setRubricHistory((prev) => [...prev, selectedRubric]);
                setSelectedRubric(rubric);
                setIsTransitioning(false);
            }, 150);
        },
        [selectedRubric]
    );

    const goBack = useCallback(() => {
        setIsTransitioning(true);
        setTimeout(() => {
            setRubricHistory((prev) => {
                const nextHistory = [...prev];
                const previousRubric = nextHistory.pop();
                setSelectedRubric(previousRubric ?? false);
                return nextHistory;
            });
            setIsTransitioning(false);
        }, 150);
    }, []);

    const goTo = useCallback(
        (url: string) => () => {
            showLoader();
            window.location.href = url;
        },
        [showLoader]
    );

    const listGenerator = useCallback(() => {
        const rubricsData = rubricator_json as RubricatorMap;
        let result: JSX.Element;
        const listItems: JSX.Element[] = [];
        let vvi = 0;

        if (!selectedRubric) {
            for (const [key, value] of Object.entries(rubricsData)) {
                listItems.push(
                    <ListItem
                        disablePadding
                        onClick={rubricHandle(value)}
                        secondaryAction={<KeyboardArrowRightIcon />}
                        sx={{
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                transform: 'translateX(4px)',
                            },
                        }}
                        key={key}
                    >
                        <ListItemButton>
                            <ListItemText slotProps={{ primary: { sx: { fontSize: '16px' } } }} primary={value.name} />
                        </ListItemButton>
                    </ListItem>
                );
                vvi++;
            }
            result = (
                <div>
                    <List sx={{ transition: 'all 0.3s ease-in-out', opacity: isTransitioning ? 0.5 : 1 }}>{listItems}</List>
                    <Box sx={{ width: '100%', padding: '16px' }}>
                        <Button
                            fullWidth
                            size="large"
                            variant="contained"
                            onClick={goTo('/catalog')}
                            sx={{
                                transition: 'all 0.3s ease-in-out',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                },
                            }}
                        >
                            Все рубрики
                        </Button>
                    </Box>
                </div>
            );
        } else {
            const selectedRubrics = selectedRubric.rubrics || {};
            for (const [key, value] of Object.entries(selectedRubrics)) {
                const hasChildren = Boolean(value.rubrics);
                listItems.push(
                    <ListItem
                        disablePadding
                        onClick={hasChildren ? rubricHandle(value) : goTo(value.url)}
                        secondaryAction={hasChildren ? <KeyboardArrowRightIcon /> : undefined}
                        sx={{
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                transform: 'translateX(4px)',
                            },
                        }}
                        key={key}
                    >
                        <ListItemButton>
                            <ListItemText slotProps={{ primary: { sx: { fontSize: '16px' } } }} primary={value.name} />
                        </ListItemButton>
                    </ListItem>
                );
                vvi++;
            }

            let urlToRubric = '';
            if (selectedRubric.kind && !selectedRubric.parent) {
                urlToRubric = selectedRubric.url;
            }

            result = (
                <div>
                    <Box sx={{ width: '100%', padding: '16px' }}>
                        <Button
                            variant="text"
                            size="medium"
                            startIcon={<ArrowBackIcon />}
                            onClick={goBack}
                            sx={{
                                padding: '6px 0px',
                                marginBottom: '16px',
                                transition: 'all 0.3s ease-in-out',
                                '&:hover': {
                                    transform: 'translateX(-4px)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                        >
                            Назад
                        </Button>
                        <Typography
                            variant="h4"
                            display="block"
                            gutterBottom
                            sx={{
                                transition: 'all 0.3s ease-in-out',
                                opacity: isTransitioning ? 0.7 : 1,
                            }}
                        >
                            {selectedRubric.name}
                        </Typography>
                        {selectedRubric.kind ? (
                            <Button
                                fullWidth
                                size="large"
                                variant="contained"
                                onClick={goTo(selectedRubric.url)}
                                sx={{
                                    transition: 'all 0.3s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                    },
                                }}
                            >
                                Все товары рубрики
                            </Button>
                        ) : (
                            <Button
                                fullWidth
                                size="large"
                                variant="contained"
                                onClick={goTo(urlToRubric)}
                                sx={{
                                    transition: 'all 0.3s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                                    },
                                }}
                            >
                                Все подрубрики
                            </Button>
                        )}
                    </Box>
                    <List sx={{ transition: 'all 0.3s ease-in-out', opacity: isTransitioning ? 0.5 : 1 }}>{listItems}</List>
                </div>
            );
        }

        return result;
    }, [selectedRubric, rubricHandle, goTo, isTransitioning, goBack]);

    const drawerList = useCallback(
        () => (
            <Box sx={{ width: getDrawerWidth() }} role="presentation">
                <Box
                    sx={{
                        width: '100%',
                        margin: '0px 0px 16px 0px',
                        padding: '16px 16px 0 16px',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <img src="/images/sp/fedsp_logo.svg" loading="lazy" style={{ height: '20px' }} />
                    <IconButton
                        aria-label="close"
                        onClick={toggleDrawer}
                        sx={{
                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                            color: 'rgba(0, 0, 0, 0.54)',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                color: 'rgba(0, 0, 0, 0.7)',
                                transform: 'rotate(90deg)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                <Box>{listGenerator()}</Box>
            </Box>
        ),
        [getDrawerWidth, toggleDrawer, listGenerator, isTransitioning]
    );

    return (
        <Fragment>
            <Backdrop
                open={loader}
                onClick={hideLoader}
                sx={{
                    zIndex: 2147483647,
                    color: '#fff',
                    transition: 'all 0.3s ease-in-out',
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Drawer
                anchor="left"
                variant="temporary"
                open={open}
                onClose={toggleDrawer}
                transitionDuration={300}
            >
                {drawerList()}
            </Drawer>
        </Fragment>
    );
};

export default App;
